// Pure projected-snapshot diff helpers for the BattlePage animation
// pipeline. Determines whether a chunk of wire events produces any
// user-visible change in the projected `GameView`; if it doesn't,
// `playEvents` skips the post-dispatch motion beat and the chunk lands
// instantly.

import type { CardView, GameView } from "../api/types";

export function sameKeywords(
  a: CardView["keywords"],
  b: CardView["keywords"],
): boolean {
  const ak = a ?? [];
  const bk = b ?? [];
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    const ai = ak[i]!;
    const bi = bk[i]!;
    if (ai.name !== bi.name) return false;
    if (ai.x !== bi.x) return false;
  }
  return true;
}

export function sameNamedCounters(
  a: CardView["named_counters"],
  b: CardView["named_counters"],
): boolean {
  const ak = a ?? [];
  const bk = b ?? [];
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    const ai = ak[i]!;
    const bi = bk[i]!;
    if (ai.name !== bi.name) return false;
    if (ai.count !== bi.count) return false;
  }
  return true;
}

// Returns true if `next` differs from `prev` in any way the user can
// see — cards moved between zones, took damage, lost life, etc. Pure
// scalar diffs (phase, turn, active player, the per-seat panel digits)
// don't need their own motion beat: the phase chip and panel digits
// carry their own CSS transitions and Motion's layoutId only kicks in
// when an eid actually moves to a new rect. Note this only decides
// the MOTION beat — phase-only chunks still hold the inter-step
// pacing floor via `needsPacingFloor` in animateSteps.ts, otherwise
// an opponent's phase cascade flashes past in one frame.
//
// `combat` is kept in the visible-diff set on purpose: the
// `attackers_declared` chunk often arrives ALONE in its own splitter
// chunk (the matching per-card `is_attacking` Attr patches land in the
// NEXT chunk), and we need that chunk to run its own beat so the
// reducer's `combat.attackers` update commits before the damage chunk
// reads it for combat-lunge attribution.
export function hasVisibleDiff(prev: GameView, next: GameView): boolean {
  if ((prev.combat == null) !== (next.combat == null)) return true;
  if (prev.combat && next.combat) {
    if (prev.combat.attackers.length !== next.combat.attackers.length) return true;
    if (prev.combat.blocks.length !== next.combat.blocks.length) return true;
  }
  for (const seat of [0, 1] as const) {
    const a = prev.players[seat];
    const b = next.players[seat];
    if (a.life !== b.life) return true;
    // Per-chunk Move events update the zone arrays but the matching
    // `*_size` scalars only arrive at the END of the batch (the
    // translator emits scalar diffs after all RawEvents). Without
    // checking array length here, a `Move(deck → hand)` chunk
    // (a draw) and other zone-only chunks register as INVISIBLE,
    // skip the motion beat, and the user sees opp's whole turn fly
    // past with no pacing.
    if ((a.hand?.length ?? 0) !== (b.hand?.length ?? 0)) return true;
    if ((a.deck?.length ?? 0) !== (b.deck?.length ?? 0)) return true;
    if ((a.graveyard?.length ?? 0) !== (b.graveyard?.length ?? 0)) return true;
    if ((a.exile?.length ?? 0) !== (b.exile?.length ?? 0)) return true;
    if (a.battle.length !== b.battle.length) return true;
    for (let i = 0; i < a.battle.length; i++) {
      const ca = a.battle[i];
      const cb = b.battle[i];
      if (ca.entity_id !== cb.entity_id) return true;
      if (ca.damage_marked !== cb.damage_marked) return true;
      if (ca.current_attack !== cb.current_attack) return true;
      if (ca.current_life !== cb.current_life) return true;
      if (ca.remaining_life !== cb.remaining_life) return true;
      if (ca.face_down !== cb.face_down) return true;
      if (ca.is_attacking !== cb.is_attacking) return true;
      if (ca.seal_counters !== cb.seal_counters) return true;
      // End-of-turn cleanup chunks often carry only keyword removes
      // (an EOT-granted Sneaky drops, a Sealed counter expires, …)
      // and no stat / zone diffs. Without these checks `applyEventBatch`
      // takes the instant path and the keyword strip snaps off in one
      // frame instead of crossfading through the dispatch's view
      // transition.
      if (!sameKeywords(ca.keywords, cb.keywords)) return true;
      if (!sameNamedCounters(ca.named_counters, cb.named_counters)) return true;
    }
  }
  return false;
}
